/**
 * The Channels and communications tab of an entity (ALC-4770): the partners enabled for the company,
 * the powers of attorney that give a channel its authority, the expiry dates, and the status of the
 * channel an obligation would use (also shown in the obligations central). Namespace `entityChannels`.
 */
export const entityChannelsPtBr = {
  intro:
    'Veja quais parceiros estão habilitados para esta empresa, em qual ambiente, e as procurações que dão poder a cada canal. As credenciais continuam na página da integração: aqui você só vê o estado e vai até ela. O ERP ainda não transmite obrigações, então "pronto" quer dizer que o canal está configurado.',
  tabLabel: 'Canais e comunicações',
  loadError: 'Não foi possível carregar os canais desta entidade.',
  retry: 'Tentar novamente',
  readOnly: {
    noPermission: 'Somente leitura: você não tem permissão para alterar os canais.',
    notHeadOffice:
      'Serpro, RTC e as procurações do contribuinte valem para toda a empresa e só podem ser alterados na matriz.',
    openHeadOffice: 'Abrir a matriz',
    noTaxpayer:
      'Esta entidade não tem contribuinte federal. Informe a identidade do contribuinte em "Informações básicas" para habilitar Serpro e RTC.',
  },
  attention: {
    ok: 'Nenhum canal ou vencimento precisa de atenção.',
    needs_one: '{{count}} item precisa de atenção',
    needs_other: '{{count}} itens precisam de atenção',
    asOf: 'Situação em {{date}}',
  },
  partner: {
    FOCUS: 'Focus NFe',
    TECNOSPEED: 'TecnoSpeed',
    SERPRO: 'Serpro',
    RTC: 'RTC',
  },
  partnerHint: {
    FOCUS: 'Emissão de NFS-e do estabelecimento',
    TECNOSPEED: 'Pagamentos e DDA do estabelecimento',
    SERPRO: 'Integra Contador: obrigações federais do contribuinte',
    RTC: 'Apuração assistida de CBS e IBS do contribuinte',
  },
  scope: {
    ENTITY: 'Estabelecimento',
    TAXPAYER: 'Contribuinte (matriz)',
  },
  environment: {
    PRODUCTION: 'Produção',
    HOMOLOGATION: 'Homologação',
  },
  enablements: {
    title: 'Parceiros habilitados',
    description: 'Cada parceiro, o ambiente e a credencial que o ERP já guarda para ele.',
    tableLabel: 'Parceiros habilitados',
    add: 'Habilitar parceiro',
    empty: {
      title: 'Nenhum parceiro habilitado',
      description:
        'Habilite o parceiro que entrega as obrigações desta empresa. A credencial é configurada na página da integração.',
    },
    columns: {
      partner: 'Parceiro',
      scope: 'Abrangência',
      environment: 'Ambiente',
      credential: 'Credencial',
      certificate: 'Certificado',
    },
    credential: {
      active: 'Em serviço',
      inactive: 'Fora de serviço',
      missing: 'Não encontrada',
      none: 'Sem credencial no ERP',
      noneHint: 'O conector deste parceiro ainda não existe no ERP.',
      environment: 'Configurada para {{environment}}',
    },
    certificate: {
      expires: 'Vence em {{date}}',
      expired: 'Vencido em {{date}}',
    },
    openIntegration: 'Abrir integração',
    openIntegrationLabel: 'Abrir a integração {{partner}}',
    remove: 'Remover',
    removeLabel: 'Remover a habilitação {{partner}} ({{environment}})',
    removeTitle: 'Remover habilitação',
    removeDescription:
      'O canal {{partner}} ({{environment}}) deixa de estar habilitado para esta empresa. A credencial na integração não é alterada.',
    removeError: 'Não foi possível remover a habilitação.',
    dialog: {
      title: 'Habilitar parceiro',
      description:
        'O ERP aponta para a credencial que a integração já guarda. Ele não guarda segredos nem cria credenciais.',
      partner: 'Parceiro',
      environment: 'Ambiente',
      scopeNote: {
        ENTITY: 'Usa a credencial deste estabelecimento.',
        TAXPAYER: 'Vale para todo o contribuinte e é registrado pela matriz.',
      },
      noCredentialNote: 'Sem credencial no ERP até o conector deste parceiro existir.',
      integrationHelp: 'Falta a credencial? Configure-a na página da integração.',
      cancel: 'Cancelar',
      submit: 'Habilitar',
      saveError: 'Não foi possível habilitar o parceiro. Tente novamente.',
    },
  },
  powers: {
    title: 'Procurações e autorizações',
    description:
      'O poder que o outorgante dá ao canal: quem recebe, quais obrigações cobre, até quando vale e a evidência.',
    tableLabel: 'Procurações e autorizações',
    add: 'Registrar procuração',
    empty: {
      title: 'Nenhuma procuração registrada',
      description: 'Registre a procuração ou autorização que dá poder ao canal.',
    },
    columns: {
      partner: 'Parceiro',
      grantee: 'Outorgado',
      coverage: 'Obrigações cobertas',
      validity: 'Vigência',
      status: 'Situação',
    },
    standing: {
      ACTIVE: 'Vigente',
      UPCOMING: 'Ainda não vigente',
      EXPIRED: 'Vencida',
    },
    validity: {
      open: 'Desde {{from}}',
      range: '{{from}} a {{to}}',
    },
    evidence: 'Evidência: {{code}} ({{description}})',
    ended: 'Encerrada: {{reason}}',
    grantor: 'Outorgante: {{name}}',
    end: 'Encerrar',
    endLabel: 'Encerrar a procuração de {{grantee}}',
    dialog: {
      title: 'Registrar procuração',
      description:
        'O outorgante é esta empresa (ou o contribuinte, para Serpro e RTC). A procuração fica registrada com a evidência e nunca é apagada.',
      partner: 'Parceiro',
      grantee: 'Outorgado',
      granteeTaxId: 'CPF ou CNPJ do outorgado',
      granteeTaxIdError: 'Informe um CPF (11 dígitos) ou um CNPJ (14 caracteres).',
      coverage: 'Obrigações cobertas',
      coverageHelp: 'Marque as obrigações que o outorgado pode cumprir por este canal.',
      validFrom: 'Início da vigência',
      validTo: 'Fim da vigência',
      validFromPlaceholder: 'dd/mm/aaaa',
      validFromError: 'Informe uma data válida.',
      validToError: 'Informe uma data válida, igual ou posterior ao início.',
      evidenceCode: 'Código da evidência',
      evidenceCodePlaceholder: 'Protocolo ou número do documento',
      evidenceDescription: 'Descrição da evidência',
      cancel: 'Cancelar',
      submit: 'Registrar',
      saveError: 'Não foi possível registrar a procuração. Tente novamente.',
    },
    endDialog: {
      title: 'Encerrar procuração',
      description:
        'Informe o último dia de vigência e o motivo. A procuração continua no histórico e a vigência não pode ser ampliada.',
      endOn: 'Último dia de vigência',
      reason: 'Motivo',
      cancel: 'Cancelar',
      submit: 'Encerrar',
      saveError: 'Não foi possível encerrar a procuração. Tente novamente.',
    },
  },
  expirations: {
    title: 'Vencimentos',
    description:
      'Certificados lidos da configuração dos parceiros e procurações, do mais próximo ao mais distante.',
    tableLabel: 'Vencimentos de certificados e procurações',
    empty: 'Nenhum certificado ou procuração com data de vencimento.',
    kind: {
      CERTIFICATE: 'Certificado',
      POWER_OF_ATTORNEY: 'Procuração',
    },
    columns: {
      kind: 'Item',
      partner: 'Parceiro',
      date: 'Vence em',
      remaining: 'Prazo',
    },
    state: {
      EXPIRED: 'Vencido',
      EXPIRING: 'Vence em breve',
      OK: 'Em dia',
    },
    daysLeft_zero: 'Vence hoje',
    daysLeft_one: 'Falta {{count}} dia',
    daysLeft_other: 'Faltam {{count}} dias',
    daysAgo_one: 'Venceu há {{count}} dia',
    daysAgo_other: 'Venceu há {{count}} dias',
  },
  status: {
    state: {
      READY: 'Pronto',
      NO_CHANNEL: 'Sem canal',
      NOT_HOMOLOGATED: 'Não homologado',
    },
    reason: {
      NO_ENABLEMENT: 'Sem habilitação',
      HOMOLOGATION_ONLY: 'Só homologação',
      NO_POWER_OF_ATTORNEY: 'Sem procuração',
      POWER_OF_ATTORNEY_EXPIRED: 'Procuração vencida',
      NO_CREDENTIAL_IN_ERP: 'Sem credencial no ERP',
      CREDENTIAL_NOT_FOUND: 'Credencial não encontrada',
      CREDENTIAL_INACTIVE: 'Credencial fora de serviço',
      CERTIFICATE_EXPIRED: 'Certificado vencido',
    },
    notHomologated: 'O ERP ainda não tem um canal homologado para esta obrigação.',
    viaPartner: 'Via {{partner}}',
    configure: 'Configurar canal',
    configureLabel: 'Configurar o canal: {{name}}',
    openChannels: 'Abrir {{name}} na aba Canais e comunicações',
  },
  errors: {
    OBLIGATION_NOT_FOUND: 'Esta obrigação não está no catálogo. Atualize a página.',
    OBLIGATION_DATE_INVALID:
      'Informe datas válidas. O fim da vigência não pode ser anterior ao início.',
    OBLIGATION_RETRY: 'Os dados mudaram durante o salvamento. Atualize a página e tente novamente.',
    CHANNEL_CREDENTIAL_NOT_FOUND:
      'Esta empresa ainda não tem credencial deste parceiro no ERP. Configure a credencial na página da integração e tente novamente.',
    CHANNEL_CREDENTIAL_ENTITY_MISMATCH:
      'Um canal só pode usar a credencial do próprio estabelecimento.',
    CHANNEL_ENABLEMENT_NOT_FOUND: 'Não encontramos esta habilitação. Atualize a página.',
    POWER_OF_ATTORNEY_DETAILS_REQUIRED:
      'Informe o nome e o documento do outorgado e a evidência, com código e descrição.',
    POWER_OF_ATTORNEY_TAX_ID_INVALID:
      'O documento do outorgado não é um CPF ou CNPJ válido. Confira os dígitos.',
    POWER_OF_ATTORNEY_COVERAGE_INVALID: 'Escolha ao menos uma obrigação que a procuração cobre.',
    POWER_OF_ATTORNEY_NOT_FOUND: 'Não encontramos esta procuração. Atualize a página.',
    POWER_OF_ATTORNEY_END_INVALID:
      'A procuração só pode ser encerrada uma vez, com motivo, em data igual ou posterior ao início e sem ampliar a vigência.',
    ESTABLISHMENT_PROFILE_ENTITY_NOT_FOUND: 'Não encontramos o estabelecimento. Atualize a página.',
    ESTABLISHMENT_PROFILE_TAXPAYER_NOT_FOUND:
      'Não encontramos o contribuinte federal. Atualize a página.',
    ESTABLISHMENT_PROFILE_TAXPAYER_REQUIRED:
      'Esta entidade não tem contribuinte federal. Informe o contribuinte em "Informações básicas".',
    ESTABLISHMENT_PROFILE_HEAD_OFFICE_ONLY:
      'Estes dados valem para todo o contribuinte. Abra a matriz para alterar.',
    ESTABLISHMENT_PROFILE_ENTITY_SCOPE_FORBIDDEN:
      'Este estabelecimento está fora do seu escopo de acesso. Peça a um administrador para ampliar o acesso.',
  },
} as const;

type Widen<T> = T extends string ? string : { [K in keyof T]: Widen<T[K]> };

export const entityChannelsEn: Widen<typeof entityChannelsPtBr> = {
  intro:
    'See which partners are enabled for this company, in which environment, and the powers of attorney that give each channel its authority. Credentials stay on the integration page: here you only see their state and go there. The ERP does not transmit obligations yet, so "ready" means the channel is set up.',
  tabLabel: 'Channels and communications',
  loadError: 'Could not load the channels of this entity.',
  retry: 'Try again',
  readOnly: {
    noPermission: 'Read only: you do not have permission to change the channels.',
    notHeadOffice:
      'Serpro, RTC and the Taxpayer powers of attorney apply to the whole company and can only be changed on the head office.',
    openHeadOffice: 'Open the head office',
    noTaxpayer:
      'This entity has no federal Taxpayer. Set the taxpayer identity in "Basic information" to enable Serpro and RTC.',
  },
  attention: {
    ok: 'No channel or expiry needs attention.',
    needs_one: '{{count}} item needs attention',
    needs_other: '{{count}} items need attention',
    asOf: 'As of {{date}}',
  },
  partner: {
    FOCUS: 'Focus NFe',
    TECNOSPEED: 'TecnoSpeed',
    SERPRO: 'Serpro',
    RTC: 'RTC',
  },
  partnerHint: {
    FOCUS: 'NFS-e issuing for the establishment',
    TECNOSPEED: 'Payments and DDA for the establishment',
    SERPRO: 'Integra Contador: the Taxpayer federal obligations',
    RTC: 'Assisted CBS and IBS assessment for the Taxpayer',
  },
  scope: {
    ENTITY: 'Establishment',
    TAXPAYER: 'Taxpayer (head office)',
  },
  environment: {
    PRODUCTION: 'Production',
    HOMOLOGATION: 'Homologation',
  },
  enablements: {
    title: 'Enabled partners',
    description: 'Each partner, the environment and the credential the ERP already keeps for it.',
    tableLabel: 'Enabled partners',
    add: 'Enable partner',
    empty: {
      title: 'No partner enabled',
      description:
        'Enable the partner that delivers this company obligations. The credential is set up on the integration page.',
    },
    columns: {
      partner: 'Partner',
      scope: 'Applies to',
      environment: 'Environment',
      credential: 'Credential',
      certificate: 'Certificate',
    },
    credential: {
      active: 'In service',
      inactive: 'Out of service',
      missing: 'Not found',
      none: 'No credential in the ERP',
      noneHint: 'The connector for this partner does not exist in the ERP yet.',
      environment: 'Set to {{environment}}',
    },
    certificate: {
      expires: 'Expires on {{date}}',
      expired: 'Expired on {{date}}',
    },
    openIntegration: 'Open integration',
    openIntegrationLabel: 'Open the {{partner}} integration',
    remove: 'Remove',
    removeLabel: 'Remove the {{partner}} enablement ({{environment}})',
    removeTitle: 'Remove enablement',
    removeDescription:
      'The {{partner}} channel ({{environment}}) stops being enabled for this company. The credential on the integration is not changed.',
    removeError: 'Could not remove the enablement.',
    dialog: {
      title: 'Enable partner',
      description:
        'The ERP points at the credential the integration already keeps. It stores no secrets and creates no credentials.',
      partner: 'Partner',
      environment: 'Environment',
      scopeNote: {
        ENTITY: 'Uses the credential of this establishment.',
        TAXPAYER: 'Applies to the whole Taxpayer and is registered by the head office.',
      },
      noCredentialNote: 'No credential in the ERP until this partner connector exists.',
      integrationHelp: 'Missing the credential? Set it up on the integration page.',
      cancel: 'Cancel',
      submit: 'Enable',
      saveError: 'Could not enable the partner. Try again.',
    },
  },
  powers: {
    title: 'Powers of attorney and authorizations',
    description:
      'The power the grantor gives the channel: who receives it, which obligations it covers, until when it is valid and the evidence.',
    tableLabel: 'Powers of attorney and authorizations',
    add: 'Register power of attorney',
    empty: {
      title: 'No power of attorney registered',
      description:
        'Register the power of attorney or authorization that gives the channel its authority.',
    },
    columns: {
      partner: 'Partner',
      grantee: 'Grantee',
      coverage: 'Obligations covered',
      validity: 'Validity',
      status: 'Status',
    },
    standing: {
      ACTIVE: 'In force',
      UPCOMING: 'Not in force yet',
      EXPIRED: 'Expired',
    },
    validity: {
      open: 'From {{from}}',
      range: '{{from}} to {{to}}',
    },
    evidence: 'Evidence: {{code}} ({{description}})',
    ended: 'Ended: {{reason}}',
    grantor: 'Grantor: {{name}}',
    end: 'End',
    endLabel: 'End the power of attorney of {{grantee}}',
    dialog: {
      title: 'Register power of attorney',
      description:
        'The grantor is this company (or the Taxpayer, for Serpro and RTC). The power stays on record with its evidence and is never deleted.',
      partner: 'Partner',
      grantee: 'Grantee',
      granteeTaxId: 'Grantee CPF or CNPJ',
      granteeTaxIdError: 'Enter a CPF (11 digits) or a CNPJ (14 characters).',
      coverage: 'Obligations covered',
      coverageHelp: 'Tick the obligations the grantee may fulfil through this channel.',
      validFrom: 'Validity start',
      validTo: 'Validity end',
      validFromPlaceholder: 'dd/mm/yyyy',
      validFromError: 'Enter a valid date.',
      validToError: 'Enter a valid date on or after the start.',
      evidenceCode: 'Evidence code',
      evidenceCodePlaceholder: 'Protocol or document number',
      evidenceDescription: 'Evidence description',
      cancel: 'Cancel',
      submit: 'Register',
      saveError: 'Could not register the power of attorney. Try again.',
    },
    endDialog: {
      title: 'End power of attorney',
      description:
        'Enter the last day it is valid and the reason. The power stays in the history and its validity cannot be extended.',
      endOn: 'Last day of validity',
      reason: 'Reason',
      cancel: 'Cancel',
      submit: 'End',
      saveError: 'Could not end the power of attorney. Try again.',
    },
  },
  expirations: {
    title: 'Expiry dates',
    description:
      'Certificates read from the partner settings and powers of attorney, soonest first.',
    tableLabel: 'Certificate and power of attorney expiry dates',
    empty: 'No certificate or power of attorney has an expiry date.',
    kind: {
      CERTIFICATE: 'Certificate',
      POWER_OF_ATTORNEY: 'Power of attorney',
    },
    columns: {
      kind: 'Item',
      partner: 'Partner',
      date: 'Expires on',
      remaining: 'Time left',
    },
    state: {
      EXPIRED: 'Expired',
      EXPIRING: 'Expiring soon',
      OK: 'Up to date',
    },
    daysLeft_zero: 'Expires today',
    daysLeft_one: '{{count}} day left',
    daysLeft_other: '{{count}} days left',
    daysAgo_one: 'Expired {{count}} day ago',
    daysAgo_other: 'Expired {{count}} days ago',
  },
  status: {
    state: {
      READY: 'Ready',
      NO_CHANNEL: 'No channel',
      NOT_HOMOLOGATED: 'Not homologated',
    },
    reason: {
      NO_ENABLEMENT: 'No enablement',
      HOMOLOGATION_ONLY: 'Homologation only',
      NO_POWER_OF_ATTORNEY: 'No power of attorney',
      POWER_OF_ATTORNEY_EXPIRED: 'Power of attorney expired',
      NO_CREDENTIAL_IN_ERP: 'No credential in the ERP',
      CREDENTIAL_NOT_FOUND: 'Credential not found',
      CREDENTIAL_INACTIVE: 'Credential out of service',
      CERTIFICATE_EXPIRED: 'Certificate expired',
    },
    notHomologated: 'The ERP has no homologated channel for this obligation yet.',
    viaPartner: 'Via {{partner}}',
    configure: 'Set up channel',
    configureLabel: 'Set up the channel: {{name}}',
    openChannels: 'Open {{name}} on the Channels and communications tab',
  },
  errors: {
    OBLIGATION_NOT_FOUND: 'This obligation is not in the catalog. Refresh the page.',
    OBLIGATION_DATE_INVALID:
      'Enter valid dates. The end of the validity cannot be before its start.',
    OBLIGATION_RETRY: 'The data changed while saving. Refresh the page and try again.',
    CHANNEL_CREDENTIAL_NOT_FOUND:
      'This company has no credential for this partner in the ERP yet. Set the credential up on the integration page and try again.',
    CHANNEL_CREDENTIAL_ENTITY_MISMATCH:
      'A channel can only use the credential of its own establishment.',
    CHANNEL_ENABLEMENT_NOT_FOUND: 'This enablement was not found. Refresh the page.',
    POWER_OF_ATTORNEY_DETAILS_REQUIRED:
      'Enter the grantee name and tax ID and the evidence, with code and description.',
    POWER_OF_ATTORNEY_TAX_ID_INVALID:
      'The grantee tax ID is not a valid CPF or CNPJ. Check the digits.',
    POWER_OF_ATTORNEY_COVERAGE_INVALID:
      'Pick at least one obligation the power of attorney covers.',
    POWER_OF_ATTORNEY_NOT_FOUND: 'This power of attorney was not found. Refresh the page.',
    POWER_OF_ATTORNEY_END_INVALID:
      'A power of attorney can only be ended once, with a reason, on or after its start and without extending it.',
    ESTABLISHMENT_PROFILE_ENTITY_NOT_FOUND: 'The establishment was not found. Refresh the page.',
    ESTABLISHMENT_PROFILE_TAXPAYER_NOT_FOUND:
      'The federal Taxpayer was not found. Refresh the page.',
    ESTABLISHMENT_PROFILE_TAXPAYER_REQUIRED:
      'This entity has no federal Taxpayer. Set the taxpayer identity in "Basic information".',
    ESTABLISHMENT_PROFILE_HEAD_OFFICE_ONLY:
      'This data applies to the whole Taxpayer. Open the head office to change it.',
    ESTABLISHMENT_PROFILE_ENTITY_SCOPE_FORBIDDEN:
      'This establishment is outside your access scope. Ask an administrator to widen your access.',
  },
};
